/**
 * Drives the standings chart's play / pause / scrub timeline.
 *
 * `visibleThrough` is a float in [0, maxMatchday]; the chart geometry interpolates the
 * line tips along it, so both the slider and the animation share one code path. The rAF
 * loop follows the cancel-on-cleanup pattern in components/worldMap/mapPosition.ts.
 *
 * State updates are throttled (≥0.05 or floored matchday change) so heavy chart parents
 * (Stats Team Points / Score Distribution) stay responsive to pause taps on mobile.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface ChartPlayback {
  visibleThrough: number;
  playing: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (value: number) => void;
}

export interface ChartPlaybackOptions {
  /** Playback speed in matchdays per second. */
  matchdaysPerSecond?: number;
}

const VISIBLE_THROUGH_EPS = 0.05;

export function useChartPlayback(maxMatchday: number, options?: ChartPlaybackOptions): ChartPlayback {
  const speed = options?.matchdaysPerSecond ?? 2.5;
  const [visibleThrough, setVisibleThrough] = useState<number>(maxMatchday);
  const [playing, setPlaying] = useState<boolean>(false);

  const rafRef = useRef<number>();
  const lastTsRef = useRef<number | undefined>(undefined);
  const valueRef = useRef<number>(visibleThrough);
  const publishedRef = useRef(visibleThrough);
  valueRef.current = visibleThrough;

  // Data is loaded once; snap the timeline to the full range when it arrives/changes.
  useEffect(() => {
    setPlaying(false);
    setVisibleThrough(maxMatchday);
    valueRef.current = maxMatchday;
    publishedRef.current = maxMatchday;
  }, [maxMatchday]);

  useEffect(() => {
    if (!playing) return undefined;
    lastTsRef.current = undefined;

    const publishIfNeeded = (next: number, force = false) => {
      const prev = publishedRef.current;
      if (
        force ||
        Math.abs(next - prev) >= VISIBLE_THROUGH_EPS ||
        Math.floor(next) !== Math.floor(prev)
      ) {
        publishedRef.current = next;
        setVisibleThrough(next);
      }
    };

    const stepFrame = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      let next = valueRef.current + dt * speed;
      if (next >= maxMatchday) {
        next = maxMatchday;
        valueRef.current = next;
        publishIfNeeded(next, true);
        setPlaying(false);
        return;
      }
      valueRef.current = next;
      publishIfNeeded(next);
      rafRef.current = requestAnimationFrame(stepFrame);
    };

    rafRef.current = requestAnimationFrame(stepFrame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, maxMatchday, speed]);

  const play = useCallback(() => {
    // Restart from the beginning if we're already at the end.
    if (valueRef.current >= maxMatchday) {
      valueRef.current = 0;
      publishedRef.current = 0;
      setVisibleThrough(0);
    }
    setPlaying(true);
  }, [maxMatchday]);

  const pause = useCallback(() => setPlaying(false), []);

  const toggle = useCallback(() => {
    setPlaying((wasPlaying) => {
      if (wasPlaying) return false;
      if (valueRef.current >= maxMatchday) {
        valueRef.current = 0;
        publishedRef.current = 0;
        setVisibleThrough(0);
      }
      return true;
    });
  }, [maxMatchday]);

  const seek = useCallback(
    (value: number) => {
      const clamped = Math.max(0, Math.min(maxMatchday, value));
      setPlaying(false);
      valueRef.current = clamped;
      publishedRef.current = clamped;
      setVisibleThrough(clamped);
    },
    [maxMatchday],
  );

  return { visibleThrough, playing, play, pause, toggle, seek };
}
